import React from 'react';
import i18n from 'i18next';
import { css } from '@emotion/core';
import Typography from '@material-ui/core/Typography';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import Collapse from '@material-ui/core/Collapse';
import Divider from '@material-ui/core/Divider';
import ShareIcon from '@material-ui/icons/Share';

import { below } from 'utils/breakpoint';
import { entryVerifyStatus } from 'config/entryVerifyStatus';
import getUrlWrapTestCode from 'ugc/utils/getUrlWrapTestCode';
import { convertProxyImgUrl } from 'client/utils/getProxyImgUrl';
import {
    isShowResultDownloadBtnGame,
    isShowUgcResultReuploadBtnGame
} from 'config/promoGameTypesClientConfig';

import {
    usePrimaryPromoSelector,
    useSecondaryPromoSelector
} from 'client/game_photoContest/hooks/useReduxSelector';
import useIsContestUgcModule from 'client/game_photoContest/hooks/useIsContestUgcModule';

import Button from 'Mui/Button';
import UgcReuploadBtn from 'client/components/Result/EntriesInfo/customResult/UgcReuploadBtn';
import ResultDownloadBtn from 'client/components/Result/EntriesInfo/customResult/ResultDownloadBtn';
import ShareDialogWrapper from 'ugc/components/ShareDialogWrapper';
import SuccessButton from 'client/game_photoUpload/PhotoUpload/SuccessButton';
import VerifyStatusLabel from 'client/components/verify/VerifyStatusLabel';
import NativeShareWrapper from 'client/components/Result/Share/NativeShareWrapper';
import UgcVoteInfo from 'client/components/Result/EntriesInfo/customResult/UgcVoteInfo';
import SerialDateInfo from 'client/components/Result/EntriesInfo/SerialDateInfo';
import RatioContainerStyle from 'components/RatioContainerStyle';
import ResponsiveUgcProxyPicture from 'client/components/ResponsiveUgcProxyPicture';
import { gameTypes } from 'config/promoGameTypes';
import ugcTypes from 'config/ugcTypes';
import VideoPlayer from 'components/VideoPlayer';

function ResultUGC({
    isExpand,
    isMultiple,
    serialNo,
    resultCreatedAt,
    componentStyle,
    blockTitle,
    validStatus,
    noNegativeMargin,
    resultInfo: { ugcAuthorName, mediaLibraryUuid, title, desc, media } = {},
    onReuploadBtnClick = () => {},
    renderCustomBtnComponent = () => false
}) {
    const isContestUgcModule = useIsContestUgcModule();
    const { id: promoId, type: gameType } = usePrimaryPromoSelector(s => s.promo);
    const { type: secondaryGameType } = useSecondaryPromoSelector(s => s.promo);
    const notPassQueryTimeStamp =
        validStatus !== entryVerifyStatus.PASSED ? `?t=${new Date().getTime()}` : '';
    const shareUrl = `${window.location.protocol}//${
        window.location.hostname
    }/ugc/${promoId}/${serialNo}${notPassQueryTimeStamp ?? ``}`;
    const ugcUrl = getUrlWrapTestCode(`${window.location.origin}/ugc/${promoId}/${serialNo}`);
    const showResultDownloadButton = isShowResultDownloadBtnGame(gameType);
    const showUgcReuploadBtn = isShowUgcResultReuploadBtnGame(gameType);

    const isVideoUpload = secondaryGameType === gameTypes.VIDEO_UPLOAD;
    const mediaData = media.filter(item => {
        if (isVideoUpload) {
            return item.type === ugcTypes.VIDEO;
        } else {
            return item.type === ugcTypes.IMAGE;
        }
    });

    const renderSubHeader = () => {
        const renderVerifyLabel = validStatus !== entryVerifyStatus.PASSED;
        const renderExpandError = !isExpand;
        if (!renderVerifyLabel && !renderExpandError) {
            return null;
        } else {
            return (
                <div css={statusContainerStyle}>
                    {renderVerifyLabel ? <VerifyStatusLabel status={validStatus} /> : <div />}
                    <div css={toggleIconStyle}>{renderExpandError && <ArrowDropDownIcon />}</div>
                </div>
            );
        }
    };
    return (
        <div css={containerStyle}>
            {Boolean(blockTitle) && (
                <Typography variant="h6" css={[blockTitleStyle, titleStyle(componentStyle)]}>
                    {blockTitle}
                </Typography>
            )}
            <SerialDateInfo id={serialNo} date={resultCreatedAt}>
                {renderSubHeader()}
                {renderCustomBtnComponent()}
                <Collapse in={isExpand} css={collapseStyle(noNegativeMargin, componentStyle)}>
                    <div>
                        {mediaData.map(({ result, id: ugcId }) => {
                            const imgUrl = convertProxyImgUrl({ url: result, resizeWidth: 1200 });
                            return imgUrl ? (
                                <div key={ugcId}>
                                    {Boolean(isVideoUpload) ? (
                                        <div css={videoWrapperStyle}>
                                            <VideoPlayer url={result} />
                                        </div>
                                    ) : (
                                        <RatioContainerStyle ratio={16 / 9} style={imgWrapperStyle}>
                                            <ResponsiveUgcProxyPicture
                                                src={imgUrl}
                                                breakPointConfigs={{
                                                    normal: { resizeWidth: 1080 }
                                                }}
                                                alt={title}
                                                enableLazyLoad
                                            />
                                        </RatioContainerStyle>
                                    )}
                                    <div css={btnWrapperStyle}>
                                        {showResultDownloadButton && (
                                            <ResultDownloadBtn
                                                href={result}
                                                componentStyle={componentStyle}
                                            />
                                        )}
                                        {showUgcReuploadBtn &&
                                            validStatus === entryVerifyStatus.NOT_PASS && (
                                                <UgcReuploadBtn
                                                    style={reuploadBtnStyle}
                                                    componentStyle={componentStyle}
                                                    onClick={onReuploadBtnClick}
                                                />
                                            )}
                                    </div>
                                </div>
                            ) : null;
                        })}
                        {isContestUgcModule &&
                            validStatus === entryVerifyStatus.PASSED &&
                            isExpand && (
                                <UgcVoteInfo
                                    mediaLibraryUUID={mediaLibraryUuid}
                                    componentStyle={componentStyle}
                                />
                            )}
                        {Boolean(ugcAuthorName) && (
                            <div css={textContainerStyle}>
                                <Typography variant="h6" css={titleStyle(componentStyle)}>
                                    {ugcAuthorName}
                                </Typography>
                            </div>
                        )}
                        {Boolean(title) && (
                            <div css={textContainerStyle}>
                                <Typography variant="h6" css={titleStyle(componentStyle)}>
                                    {title}
                                </Typography>
                            </div>
                        )}
                        {Boolean(desc) && (
                            <div css={textContainerStyle}>
                                <Typography variant="subtitle1" css={descStyle(componentStyle)}>
                                    {desc}
                                </Typography>
                            </div>
                        )}
                        <div css={btnConatinerStyle}>
                            <a
                                css={linkStyle(componentStyle)}
                                alt={title}
                                target="_blank"
                                rel="noopener noreferrer"
                                href={ugcUrl}
                            >
                                <SuccessButton
                                    componentStyle={componentStyle}
                                    style={toPortfolioBtnStyle}
                                >
                                    {i18n.t('button.go_to_work_page')}
                                </SuccessButton>
                            </a>
                            <NativeShareWrapper title={title} url={shareUrl}>
                                <ShareDialogWrapper url={shareUrl}>
                                    {({ onOpen }) => (
                                        <Button
                                            css={[shareBtnStyle]}
                                            variant="outlined"
                                            onClick={onOpen}
                                            startIcon={<ShareIcon />}
                                        >
                                            {i18n.t('label.share_media_work')}
                                        </Button>
                                    )}
                                </ShareDialogWrapper>
                            </NativeShareWrapper>
                        </div>
                    </div>
                </Collapse>
                {isMultiple && <Divider />}
            </SerialDateInfo>
        </div>
    );
}

export default ResultUGC;

const containerStyle = css`
    width: 100%;
`;

const blockTitleStyle = css`
    margin-top: 8px;
`;

const textContainerStyle = css`
    padding: 22px 8px;
`;

const titleStyle = (componentStyle = {}) => css`
    font-weight: 500;
    color: ${componentStyle.titleColor};
    word-break: break-all;
`;

const descStyle = (componentStyle = {}) => css`
    min-height: 76px;
    white-space: pre-wrap;
    color: ${componentStyle.descColor};
    word-break: break-all;
`;

const btnConatinerStyle = css`
    display: flex;
    justify-content: center;
    padding: 16px 8px;
    & button {
        color: inherit;
    }
`;

const statusContainerStyle = css`
    padding: 8px 0;
    display: flex;
    justify-content: space-between;
`;

const toggleIconStyle = css`
    display: flex;
    align-items: center;
`;

const collapseStyle = noNegativeMargin => css`
    padding: 0 24px;
    ${noNegativeMargin ? '' : 'margin: 0 -24px;'}
    ${below('sm')} {
        ${noNegativeMargin ? '' : 'margin: 0 -16px;'}
    }
`;

const linkStyle = (componentStyle = {}) => css`
    text-decoration: none;
    color: ${componentStyle.buttonTextColor};
    margin-right: 16px;
`;

const shareBtnStyle = css`
    font-size: 16px;
    padding: 8px 24px;
    min-width: 120px;
    border-radius: 28px;
    white-space: nowrap;
    border-color: inherit;
`;

const toPortfolioBtnStyle = css`
    font-size: 16px;
    padding: 8px 24px;
`;

const btnWrapperStyle = css`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 20px;
`;

const reuploadBtnStyle = css`
    margin-left: 16px;
`;

const imgWrapperStyle = css`
    width: 90%;
    margin: 16px auto 0;
    & .fever-ratio-container-content {
        display: flex;
        justify-content: center;
        & img {
            width: 100%;
            object-fit: contain;
        }
    }
`;
const videoWrapperStyle = css`
    width: 90%;
    margin: 16px auto 0;
    height: 457px;
    ${below('xsm')} {
        width: 100%;
        height: 179px;
    }
`;
